import { useCallback, useEffect, useRef } from 'react';
import pMap from 'p-map';
import { useMutation } from '@tanstack/react-query';
import { FigmaCompositorView } from '@/components/Recipe/Views/FigmaCompositorView';
import { FIGMA_BASE_URL } from '@/services/figma';
import { FigmaCompositorData } from '@/types/node';
import {
  getOrCreateFigmaApiClient,
  WeavyCompositorThumbnail,
} from '../../Recipe/FlowComponents/Editor/FigmaCompositor/figmaApiClient';
import type { EvaluatedImage, EvaluatedValue } from '@/designer/designer';
import type { InputChange } from '../../Recipe/FlowContext';
import type { DataType } from 'web';

export type ExternalFigmaNode = {
  node_id: string;
  name: string;
  node_type: string;
  locked: boolean;
  children: ExternalFigmaNode[];
};

export const VALID_FIGMA_TEXT_NODES = ['TEXT'];
export const VALID_FIGMA_IMAGE_FILL_NODES = ['FRAME', 'ROUNDED_RECTANGLE', 'SYMBOL', 'RECTANGLE'];

export const deDupeInputDisplayNameAndId = (baseName: string, existingIds: string[]): string => {
  let name = baseName;
  let suffix = 2;
  while (existingIds.includes(name)) {
    name = `${baseName}_${suffix++}`;
  }
  return name;
};

export const isValidNode = (nodeData: ExternalFigmaNode): boolean => {
  const { node_type, locked } = nodeData;
  return !locked && (VALID_FIGMA_IMAGE_FILL_NODES.includes(node_type) || VALID_FIGMA_TEXT_NODES.includes(node_type));
};

export const figmaNodeTypeToValidInputTypes = (nodeType: string): DataType[] => {
  if (VALID_FIGMA_TEXT_NODES.includes(nodeType)) return ['text'];
  if (VALID_FIGMA_IMAGE_FILL_NODES.includes(nodeType)) return ['image'];
  return [];
};

export const NODE_ID_STANDARD_SEPARATOR = ':';
export const NODE_ID_URL_FORMATTING_SEPARATOR = '-';

const META_START = '<!--(figmeta)';
const META_END = '(/figmeta)-->';
const ESCAPED_META_START = '&lt;!--(figmeta)';
const ESCAPED_META_END = '(/figmeta)--&gt;';

// Convert node_id from API format (colon-delimited) to URL format (hyphen-delimited)
// e.g., "123:456" -> "123-456"
export const getFormattedNodeId = (nodeId: string) => {
  const regex = new RegExp(NODE_ID_STANDARD_SEPARATOR, 'g');
  return nodeId.replace(regex, NODE_ID_URL_FORMATTING_SEPARATOR);
};

export const getFigmaFileUrl = (fileKey: string, nodeId: string) => {
  return FIGMA_BASE_URL + '/file/' + fileKey + '?node-id=' + nodeId;
};

type FigmaPastedMetadata = { fileKey: string; selectedNodeData: string };

export const getFigmaPastedMetadata = (html: string, isFigmaCompositorEnabled: boolean): FigmaPastedMetadata | null => {
  let metaStartIdx = html.indexOf(META_START);
  let metaEndIdx = html.indexOf(META_END);
  let startLen = META_START.length;

  // Handles both unescaped chars from Cmd+C path and escaped chars from right click > "Copy" menu actions
  // (Browsers HTML escape the characters in the navigator API)
  if (metaStartIdx < 0 || metaEndIdx < 0) {
    metaStartIdx = html.indexOf(ESCAPED_META_START);
    metaEndIdx = html.indexOf(ESCAPED_META_END);
    startLen = ESCAPED_META_START.length;
  }

  if (metaStartIdx < 0 || metaEndIdx < 0 || !isFigmaCompositorEnabled) return null;

  try {
    const metaBase64 = html.slice(metaStartIdx + startLen, metaEndIdx);
    const metaJson = decodeURIComponent(escape(atob(metaBase64)));
    return JSON.parse(metaJson) as FigmaPastedMetadata;
  } catch {
    return null;
  }
};

export const PASTED_FIGMA_NODE_OFFSET = 320;

export const getValidPastedFigmaNodes = (
  html: string,
  isFigmaCompositorEnabled: boolean,
): { fileKey: string; nodeId: string }[] => {
  const metadata = getFigmaPastedMetadata(html, isFigmaCompositorEnabled);
  if (!metadata) return [];
  const { fileKey, selectedNodeData } = metadata;
  return selectedNodeData
    .split(',')
    .map((nodeData) => {
      const [nodeId, nodeType, lockedFlag] = nodeData.split('|');
      return { nodeId, nodeType, isLocked: lockedFlag === '1' };
    })
    .filter(({ isLocked }) => !isLocked)
    .map(({ nodeId }) => ({ fileKey, nodeId }));
};

type ImageFileValue = { type: 'image'; value: Extract<EvaluatedImage, { type: 'file' }> };
type TextValue = { type: 'string'; value: string };

// TODO: 'computed' type input support
// https://app.asana.com/1/10497086658021/project/1213205572601284/task/1213455736973407
const isImageValue = (value: EvaluatedValue): value is ImageFileValue =>
  value.type === 'image' && value.value.type === 'file';

const isTextValue = (value: EvaluatedValue): value is TextValue => value.type === 'string';

/**
 * Ensures Weavy input image is synced correctly with Figma depending on handle state.
 * - No figmaNodeId → create a new Figma node with the image as a fill
 * - Has figmaNodeId + paintId → UPDATE the existing fill in place
 * - Has figmaNodeId, no paintId → CREATE a new fill (e.g. handle mapped via paste), then store paintId
 */
const reconcileImageFill = async (
  fileKey: string,
  figmaRootNodeId: string,
  inputId: string,
  value: ImageFileValue,
  view: FigmaCompositorView,
): Promise<void> => {
  const figmaClient = getOrCreateFigmaApiClient();
  const figmaNodeId = view.getFigmaNodeId(inputId);
  const paintId = view.getFigmaPaintId(inputId);

  let thumbnail: WeavyCompositorThumbnail | undefined;
  // Input has no corresponding Figma node → create a new fill layer for the node
  if (!figmaNodeId) {
    const result = await figmaClient.createImageLayer(
      fileKey,
      figmaRootNodeId,
      inputId,
      value.value.url,
      value.value.width,
      value.value.height,
    );
    view.setFigmaNodeId(inputId, result.id);
    view.setFigmaPaintId(inputId, result.fills[0].id);

    thumbnail = result.weavyConnections?.compositor?.thumbnail;
  } else {
    // Input has a corresponding Figma node → update the node by creating or updating the fill layer
    const fill = paintId
      ? ({ action: 'UPDATE', type: 'IMAGE', paint_id: paintId, url: value.value.url } as const)
      : ({ action: 'CREATE', type: 'IMAGE', url: value.value.url } as const);
    const result = await figmaClient.updateNode(fileKey, figmaNodeId, { fill });

    const newPaintId = result.weavyConnections?.input?.paintId;
    if (newPaintId && newPaintId !== paintId) {
      view.setFigmaPaintId(inputId, newPaintId);
    }

    thumbnail = result.weavyConnections?.compositor?.thumbnail;
  }

  if (thumbnail) {
    view.setOutputImage({
      ...thumbnail,
      thumbnailUrl: thumbnail.url,
      publicId: undefined,
      id: undefined,
    });
  }
};

/**
 * Ensures Weavy input text is synced correctly with Figma depending on handle state
 * - No figmaNodeId → create a new Figma text node
 * - Has figmaNodeId → update the text content
 */
const reconcileTextContent = async (
  fileKey: string,
  figmaRootNodeId: string,
  inputId: string,
  value: TextValue,
  view: FigmaCompositorView,
): Promise<void> => {
  const figmaClient = getOrCreateFigmaApiClient();
  const figmaNodeId = view.getFigmaNodeId(inputId);

  let thumbnail: WeavyCompositorThumbnail | undefined;
  if (!figmaNodeId) {
    const result = await figmaClient.createTextLayer(fileKey, figmaRootNodeId, inputId, value.value);

    view.setFigmaNodeId(inputId, result.id);
    thumbnail = result.weavyConnections?.compositor?.thumbnail;
  } else {
    const result = await figmaClient.updateNode(fileKey, figmaNodeId, { text: { content: value.value } });
    thumbnail = result.weavyConnections?.compositor?.thumbnail;
  }

  if (thumbnail) {
    view.setOutputImage({
      ...thumbnail,
      thumbnailUrl: thumbnail.url,
      publicId: undefined,
      id: undefined,
    });
  }
};

const syncInputChange = async (
  fileKey: string,
  figmaRootNodeId: string,
  change: InputChange,
  view: FigmaCompositorView,
): Promise<void> => {
  switch (change.changeType) {
    case 'deleted':
      view.setFigmaNodeId(change.inputId, null);
      view.setFigmaPaintId(change.inputId, null);
      // TODO: DELETE layer from Figma API
      return;
    case 'added':
    case 'updated':
      if (isImageValue(change.value)) {
        await reconcileImageFill(fileKey, figmaRootNodeId, change.inputId, change.value, view);
      } else if (isTextValue(change.value)) {
        await reconcileTextContent(fileKey, figmaRootNodeId, change.inputId, change.value, view);
      }
      return;
  }
};

const syncInputChanges = async (
  options: FigmaCompositorData['options'],
  changes: InputChange[],
  signal: AbortSignal,
  view: FigmaCompositorView,
  setIsRefreshing: (v: boolean) => void,
): Promise<boolean> => {
  const { fileKey, nodeId: figmaRootNodeId } = options;
  if (!fileKey || !figmaRootNodeId || !changes.length) return false;

  setIsRefreshing(true);
  try {
    await pMap(changes, (change) => syncInputChange(fileKey, figmaRootNodeId, change, view), { signal });
    return true;
  } catch (error) {
    if (signal.aborted) return false;
    throw error;
  } finally {
    if (!signal.aborted) setIsRefreshing(false);
  }
};

export const useSyncToFigma = (
  view: FigmaCompositorView,
  options: FigmaCompositorData['options'],
  setIsRefreshing: (v: boolean) => void,
) => {
  const controllerRef = useRef<AbortController>(undefined);

  const { mutate } = useMutation({
    mutationFn: ({ changes, signal }: { changes: InputChange[]; signal: AbortSignal }) =>
      syncInputChanges(options, changes, signal, view, setIsRefreshing),
  });

  const triggerSync = useCallback(
    (changes: InputChange[]) => {
      controllerRef.current?.abort();
      controllerRef.current = new AbortController();
      mutate({ changes, signal: controllerRef.current.signal });
    },
    [mutate],
  );

  useEffect(() => {
    return () => controllerRef.current?.abort();
  }, []);

  return triggerSync;
};
